import { useData } from '../context/data.context';

const PLAN_LEVELS: Record<string, number> = {
  Free: 0,
  Basic: 1,
  Advanced: 2,
  Professional: 3,
  Enterprise: 4
};

const GRACE_PERIOD_DAYS = 10;

/** Maximum days of history allowed for data viewing per plan. Null means unlimited, 0 means no access. */
const PLAN_DATA_DAYS: Record<string, number | null> = {
  Free: 0,
  Basic: 7,
  Advanced: 30,
  Professional: 90,
  Enterprise: null
};

export type PlanStatus = 'free' | 'active' | 'expiring-soon' | 'grace-period' | 'expired';

// eslint-disable-next-line jsdoc/require-jsdoc
function getDaysUntilExpiry(endDate: string): number {
  const end = new Date(endDate).getTime();
  const now = Date.now();
  return Math.ceil((end - now) / (1000 * 60 * 60 * 24));
}

/**
 * Hook to get the current user's plan and check feature access.
 * Accounts for a 10-day grace period after subscription expiry.
 * @returns Plan info including status, days remaining, and access checker.
 */
export default function usePlan(): {
  planName: string;
  planLevel: number;
  planStatus: PlanStatus;
  daysUntilExpiry: number | null;
  graceDaysRemaining: number | null;
  maxDataDays: number | null;
  hasAccess: (requiredPlan: string) => boolean;
} {
  const { activePaypalSubscription } = useData();

  let planStatus: PlanStatus = 'free';
  let daysUntilExpiry: number | null = null;
  let graceDaysRemaining: number | null = null;
  let effectivePlanName = 'Advanced';

  if (activePaypalSubscription?.endDate) {
    const days = getDaysUntilExpiry(activePaypalSubscription.endDate);
    daysUntilExpiry = days;

    if (days > 10) {
      planStatus = 'active';
      effectivePlanName = activePaypalSubscription.planName;
    } else if (days > 0) {
      // Within 10 days of expiry — warn but still active
      planStatus = 'expiring-soon';
      effectivePlanName = activePaypalSubscription.planName;
    } else if (days > -GRACE_PERIOD_DAYS) {
      // Past endDate but within grace period
      planStatus = 'grace-period';
      effectivePlanName = activePaypalSubscription.planName;
      graceDaysRemaining = GRACE_PERIOD_DAYS + days; // days is negative here
    } else {
      // Past grace period — treat as Free
      planStatus = 'expired';
      effectivePlanName = 'Free';
    }
  } else if (activePaypalSubscription) {
    // Subscription exists but no endDate (e.g. Enterprise)
    planStatus = 'active';
    effectivePlanName = activePaypalSubscription.planName;
  }

  const planLevel = PLAN_LEVELS[effectivePlanName] ?? 0;

  // eslint-disable-next-line jsdoc/require-jsdoc
  const hasAccess = (requiredPlan: string): boolean => {
    const required = PLAN_LEVELS[requiredPlan] ?? 0;
    return planLevel >= required;
  };

  // Preserve a known plan's configured value (Enterprise === null === unlimited);
  // only fall back to 0 (no access) for unrecognised plan names. Using `?? 0` here
  // would wrongly collapse Enterprise's null to 0.
  const maxDataDays = effectivePlanName in PLAN_DATA_DAYS
    ? PLAN_DATA_DAYS[effectivePlanName]
    : 0;

  return {
    planName: effectivePlanName,
    planLevel,
    planStatus,
    daysUntilExpiry,
    graceDaysRemaining,
    maxDataDays,
    hasAccess
  };
}
